import type { ChamberRow, ModuleSnapshot, Sample, TrayCell } from '@ctcells/contract'
import { isModuleKey } from '@ctcells/core'
import { intlLocale, maskPii, moduleLabel, serverText, t, type MessageKey } from '@ctcells/i18n'

import type { TrayCellView, TrayVerdict } from '../../figures/index.ts'
import { statusKind } from '../../atoms/statusKind.ts'
import { activeModules } from '../moduleService.ts'
import type { MetricItem } from '../viewTypes.ts'

import { poseValueOf } from './robotPoseModel.ts'
import type { StageTally } from './sampleFlowModel.ts'

/**
 * 모니터링 화면이 그리는 값 — **서버가 준 것만 읽는다.**
 *
 * 🔴 여기 있는 것은 *표시 파생*이지 상태 추론이 아니다. 버튼 활성·명령 가능 여부를 만들지 않는다.
 *    값이 없으면 지어내지 않고 `—` 로 떨어뜨린다(🔸 미확정 값은 자리만 잡는다).
 */

/** 모니터링이 다루는 장비 키. 3 모듈 + 모듈 사이를 잇는 6축 로봇 */
export type MonitoringDeviceKey = 'cent' | 'asm' | 'stain' | 'robot'

/**
 * 모니터링 장비 판의 장비 이름 — 🔴 6축 로봇만 「운송 로봇」(`monitoring.deviceSelect.robot`)이다(eric 2026-10-13 「6축 로봇 대신에.. 운송 로봇이라고..
 * 탭 부분 말이야」 → 같은 날 「운송 로봇 모니터링 페이지에. 6축 로봇이라고 되어 있는데.. 이거 운송 로봇이라고 수정해줘」). 칩 · 판 제목 · 단계 띠 ·
 * 그림 접근 이름이 이 한 곳을 읽는다. 셋은 장비 이름(`moduleLabel`) 그대로다.
 * ⚠ `module.robot`(「6축 로봇」)은 판 밖(작업 줄 · 알람 · 기록 · 모듈 목록 · 장비 관점 앱)이 그대로 쓴다 — 통째로 바꾸지 않았다.
 */
export function monitoringDeviceName(key: MonitoringDeviceKey): string {
  return key === 'robot' ? t('monitoring.deviceSelect.robot') : moduleLabel(key)
}

/**
 * URL 조각(`/monitoring/:moduleKey`)을 장비 키로 읽는다. 모르는 값이면 `undefined` —
 * 화면이 임의로 한 장비를 고르지 않고 기본 화면으로 떨어진다.
 * 콘솔·태블릿 라우트가 같은 규칙을 쓰므로 판정을 여기 한 곳에 둔다.
 */
export function monitoringDeviceKey(raw: string | undefined): MonitoringDeviceKey | undefined {
  if (raw === 'robot') return 'robot'
  return raw !== undefined && isModuleKey(raw) ? raw : undefined
}

/** 화면 키 → 계약 `ModuleSnapshot.module_type`. 인스턴스 ID(`centrifuge-1`)에 기대지 않는다 */
const MODULE_TYPE: Record<MonitoringDeviceKey, string> = {
  cent: 'centrifuge',
  asm: 'assembler',
  stain: 'staining',
  robot: 'collaborative_6axis',
}

/**
 * 계약 `module_type` 원값 → 화면 키. **위 표를 뒤집은 것이라 표는 여전히 하나다.**
 *
 * 🔴 **`module_id` 문자열에서 종류를 읽어내지 않는다.** ID 꼴(`staining-1`)은 계약이 정한 것이
 *    아니라 등록할 때 사람이 붙인 이름이다 — 앞자리를 자르는 구현으로 바꾸지 마라.
 * 🔴 **모르는 종류는 `undefined`** 다. 계약에 있으나 화면이 없는 것(`ceiling_3axis`)이 실제로
 *    있고, 그런 값이 오면 **아무 장비도 고르지 않는다**(한 대를 임의로 고르면 거짓이다).
 *
 * ⚠ 새 표를 만들지 않으려고 여기를 골랐다. `@ctcells/i18n` 의 `moduleTypeLabel` 도 같은 대응을
 *   쥐고 있지만 그쪽 결과는 **한글 표기**이고 키 종류도 더 넓다(`vision`·`safety`·`system`).
 *   화면 키(`MonitoringDeviceKey`)가 필요한 자리는 이 파일이 원본이다.
 */
export function deviceKeyOfModuleType(
  moduleType: string | null | undefined,
): MonitoringDeviceKey | undefined {
  if (moduleType == null) return undefined
  for (const [key, value] of Object.entries(MODULE_TYPE)) {
    if (value === moduleType) return key as MonitoringDeviceKey
  }
  return undefined
}

/**
 * 그 종류의 모듈 하나를 고른다.
 *
 * 🔴 **먼저 폐기(`service_state === 'terminated'`)를 걷어낸다**(2026-09-20 — 백엔드가
 *    `ModuleSnapshot` 에 그 값을 실으면서 BR-01 이 풀렸다). 종전에는 그 축이 없어
 *    「`stale` 아닌 쪽 우선」이라는 **임시 규칙으로 폐기를 우회**하고 있었다 — 그건
 *    **고장과 폐기를 같은 것으로 본 것**이라 살아 있는 장비가 응답이 늦으면 틀렸다.
 * ⚠ 값이 없으면 `active` 로 본다(`moduleService.isTerminated` 주석) — 서버 판이 낮아
 *   안 오는 경우에 **있는 장비를 지우지 않는다.**
 *
 * 🔴 **그래도 고르기 규칙은 남는다.** 폐기를 걷어내도 **같은 종류가 진짜로 2대**일 수 있다
 *    (계약이 막지 않는다). 규칙은 둘이고 **결정적이다** — 응답 순서에 기대지 않는다.
 *      1. `stale` 이 아닌 것을 우선한다
 *      2. 전부 stale 이면 `last_seen` 이 **가장 최근**인 것 (없으면 가장 먼저 온 것)
 *    ⚠ 2대를 구분해 고르는 축(사용자 선택 · 대표 지정)은 계약에도 화면에도 아직 없다.
 *
 * 🔴 **이름(`mock-` 접두어)으로 거르지 않는다.** 이름으로 상태를 추론하는 것은 규약 위반이고,
 *    운영에서 접두어가 바뀌면 조용히 틀린다. 판정은 서버가 준 «값»으로만 한다.
 */
export function findModule(
  modules: readonly ModuleSnapshot[],
  key: MonitoringDeviceKey,
): ModuleSnapshot | undefined {
  const type = MODULE_TYPE[key]
  const sameType = activeModules(modules).filter((m) => m.module_type === type)
  if (sameType.length <= 1) return sameType[0]

  const live = sameType.filter((m) => !m.stale)
  const pool = live.length > 0 ? live : sameType
  // 🔴 `reduce` 로 «가장 최근» 하나를 고른다. 정렬을 쓰면 동점일 때 순서가 엔진에 달린다
  return pool.reduce((best, m) => (seenAt(m) > seenAt(best) ? m : best))
}

/** `last_seen` 비교용 수치. 없거나 못 읽는 값은 **가장 오래된 것**으로 본다(지어내지 않는다) */
function seenAt(m: ModuleSnapshot): number {
  const raw = m.last_seen
  if (raw == null || raw === '') return Number.NEGATIVE_INFINITY
  const at = Date.parse(raw)
  return Number.isNaN(at) ? Number.NEGATIVE_INFINITY : at
}

/**
 * **모듈이 스스로 섰는가** — `ModuleSnapshot.hold.kind` 원값. 안 섰으면 `undefined`.
 *
 * 🔴 **`state` 와 다른 축이다.** 실물에서 염색기가 `state: DISPENSING` 인 채로
 *    `hold: {kind: "reagent_exhausted", since: …}` 로 섰다(2026-09-22 실측). 두 값 다 서버가
 *    준 사실이라 **한쪽이 다른 쪽을 덮지 않는다** — 화면은 둘을 나란히 적는다.
 * 🔴 **`none` 은 「안 섰다」다.** 계약 enum 에 그 값이 있고(`none`·`user_pause`·`safety_stop`·
 *    `reagent_exhausted`·`tip_exhausted`) 실물이 평소에 `{kind: "none"}` 을 준다 — 그것을 보류로 읽으면
 *    화면이 늘 「보류」라고 말한다.
 * 🔴 **모르는 값은 «그대로» 낸다.** 계약에 새 갈래가 생겨도 화면이 침묵하지 않게 한다
 *    (표기는 `enumLabel` 이 사전에 없으면 원값을 낸다).
 * ⚠ `null`·키 부재는 「모른다」이므로 보류로 세우지 않는다 — 없는 정지를 그리지 않는다.
 */
export function holdKindOf(snapshot: ModuleSnapshot | undefined): string | undefined {
  const kind: string | null | undefined = snapshot?.hold?.kind
  if (kind == null || kind === '' || kind === 'none') return undefined
  return kind
}

/**
 * **그 모듈의 활성 알람 수.** 🔴 `0`·`null`·키 부재는 `undefined` 다 — 화면이 「알람 0건」이라는
 * 빈 말을 하지 않게 한다. 「0건」과 「모른다」를 굳이 가르지 않는 자리다(둘 다 적을 것이 없다).
 *
 * ⚠ **REST 가 권위다**(`GET /modules`). 구독 미러는 이 칸을 **늘 `null`** 로 준다
 *   (`@ctcells/api` `stream/bridge.ts` 머리말 — 실측 모듈 4대 전부).
 */
export function activeAlarmCount(snapshot: ModuleSnapshot | undefined): number | undefined {
  const count = snapshot?.active_alarm_count
  return typeof count === 'number' && count > 0 ? count : undefined
}

/** 공정 칸의 상태 다섯 — 시안 `stage--done | run | wait | pause | stop` 그대로 */
export type StageState = 'done' | 'run' | 'wait' | 'pause' | 'stop'

/**
 * 공정 칸의 상태 — **있는 값 둘(모듈 스냅샷 · 셈)로만** 판정한다. 새 데이터를 만들지 않는다.
 *
 *   pause — 모듈이 스스로 섰거나(`hold`) 일시정지 · 복구 중(주의 — 사람이 볼 일)
 *   stop  — 오류 · 안전정지 · 정지(모듈이 안 돈다)
 *   run   — 모듈이 작업 중(`statusKind` 의 `run` — 실행 중 · 분주 중 · 이동 중 · 로봇 회수 대기 …)
 *   done  — 안 도는데 셈이 다 찼다(`marked === total > 0`)
 *   wait  — 그 밖(대기 · 개입 대기 · 스냅샷 없음)
 *
 * 🔴 차례가 규칙이다 — 섰다는 사실(pause · stop)이 셈(done)보다 앞이다. 다 찬 뒤 오류로 선 모듈은 «멈춤»이지 «완료»가 아니다.
 * ⚠ 모듈 상태 이름을 여기서 직접 비교하지 않는다 — 국면은 `statusKind`(`06 §4.2a`)가 접는다.
 */
export function stageStateOf(
  snapshot: ModuleSnapshot | undefined,
  tally: StageTally | undefined,
): StageState {
  const kind = statusKind(snapshot?.state ?? '')
  if (holdKindOf(snapshot) !== undefined || kind === 'pause' || kind === 'recovery') return 'pause'
  if (kind === 'err' || kind === 'stop') return 'stop'
  if (kind === 'run') return 'run'
  if (tally !== undefined && tally.total > 0 && tally.marked >= tally.total) return 'done'
  return 'wait'
}

/**
 * 서버 `Sample` → 챔버 행. **live 가 시료를 그릴 수 있는 전부다.**
 *
 * 🔴 **`slot`·`position`·`verdict` 를 채우지 않는다.** 서버에 그 축이 없다(`ChamberRow` 머리말).
 *    쵸단 실측(2026-09-20 · 배치 `2ef03051-…` 도는 중 `GET /samples` **126 스냅샷 전수**)이
 *    확인했다 — `current_position` 은 **도는 중에도** 한 스냅샷에도 나타나지 않는다.
 *    즉 `exclude_none` 으로 가려진 것이 아니라 **그 칸이 채워지지 않는다.**
 * 🟢 채우는 것은 넷이다 — `sample_id` · `stage` · `status` · `reference`, 그리고 모듈 축
 *    `current_module`.
 * ⚠ **대조 시료 판정은 서버 값이다**(`sample_type === 'Reference'`). 이름 규칙(`REF-` 접두어)으로
 *   추측하지 않는다 — 실물 대조 시료는 `sample_id` 가 `FE-0920-09` 였고 접두어가 없었다.
 * 🔴 **`status` 는 거르지 않고 그대로 나른다.** 「끝난 시료인가」를 여기서 버리면 다음 사람이
 *    그 축이 없는 줄 안다 — 거르는 일은 그 물음을 실제로 묻는 자리(`samplesAt`)가 한다.
 */
export function chamberRows(samples: readonly Sample[]): ChamberRow[] {
  return samples.map((sample) => ({
    sample_id: sample.sample_id,
    stage: sample.stage,
    status: sample.status,
    reference: sample.sample_type === 'Reference',
    ...(sample.current_module == null ? {} : { current_module: sample.current_module }),
  }))
}

/**
 * 그 모듈에 있는 시료. 🔴 **판정은 서버가 준 `current_module`(인스턴스 ID) 하나다.**
 *
 * ⚠ 모듈 «종류»로 맞추지 않는다 — 같은 종류가 2대면 둘의 시료가 한 덩어리로 섞인다.
 *   (`findModule` 이 고른 스냅샷의 `module_id` 와 맞춘다)
 *
 * 🔴 **모듈 축이 «한 행에도» 없으면 거르지 않고 전부 준다.** 그 축을 모르는 목록(= demo
 *    시나리오 · 서버가 `current_module` 을 안 실은 옛 응답)을 빈 배열로 접으면 **있는 시료가
 *    화면에서 통째로 사라진다.** 「모른다」를 「없다」로 읽지 않는 것이 이 파일의 규율이다
 *    (`whereOf` 가 `undefined` 를 내는 것과 같은 축).
 * ⚠ 반대로 **축이 있는데 맞는 행이 0건이면 0건이 정답이다** — 그 장비에 시료가 없는 것이다.
 *
 * ## 🔴 두 번째 축 — **끝난 시료는 그 장비에 «있지» 않다**(2026-09-21)
 *
 * 서버가 종착·폐기 뒤에도 `current_module` 을 **비우지 않는다.** 백엔드 자신이 그렇게 적어 뒀다:
 * 「`current_module` 을 비우는 자리가 어디에도 없어 … 앞 배치의 시료가 수거 뒤에도 그 모듈에
 * 남아 보인다」(`../ctcells-core/app/services/tray_confirm.py` `_batch_of_module` 머리말).
 * 그래서 모듈 축만으로 세면 **어제 끝난 시료까지 들어간다** — 실측(2026-09-21 `GET /samples` 77건)에서
 * `staining-1` 이 46건이었고 **그중 37건이 `completed`** 였다.
 *
 * ⇒ **`status === 'in_process'` 만 남긴다**(eric 결정 2026-09-21). 「이 장비에 **지금** 있는 시료」가
 *    카드 숫자와 옆 표가 뜻하는 것이고, `completed`·`discarded` 는 그 물음의 답이 아니다.
 * ⚠ **배치로 좁히지 않는다.** 장비 관점의 물음은 「지금 이 장비에 뭐가 있나」라서 도는 작업과 무관하다.
 * ⚠ 이 거르기는 **모듈 축이 없어도 적용된다** — 「모듈을 모른다」와 「끝났다」는 다른 축이고,
 *   뒤는 언제나 알 수 있다(`status` 는 계약 required · 실측 77/77 present).
 * 🔴 서버가 종착에서 `current_module` 을 비워 주면 이 거르기는 **군더더기가 되지 무해해진다**
 *    (BR-52). 그때도 지우지 마라 — 폐기 시료는 여전히 걸러야 한다.
 */
export function samplesAt(
  chambers: readonly ChamberRow[],
  snapshot: ModuleSnapshot | undefined,
): readonly ChamberRow[] {
  const here = chambers.filter((row) => row.status === 'in_process')
  if (!here.some((row) => row.current_module !== undefined)) return here
  if (snapshot === undefined) return []
  return here.filter((row) => row.current_module === snapshot.module_id)
}

/**
 * C 트레이의 물리 칸 배치 — 행 순서 `[A1 A2 A3][B1 B2 B3][C1 C2 C3]`.
 *
 * 🔴 **칸 이름은 서버 웰 이름이다**(eric 2026-09-28 「하나를 기준으로 통일시켜줘」). 레시피
 *    `labware[].wells` 의 키 · 배치 `sample_assignments[].well` · 모니터링 배정 웰 칩이 전부 이
 *    이름(행 글자 + 열 숫자)을 쓴다. ⚠ 와이어프레임 표기는 `[1a 2a 3a][1b 2b 3b][4a 4b R]` 였다 —
 *    자리는 격자 위치 그대로 옮겼다(와이어프레임 1959행 `POS` 표와 같다). 옛 표기를 되살리지 마라.
 *
 * 🔴 **이것은 랩웨어 «규격»이지 서버 응답이 아니다.** 칸이 9개이고 그 이름이 이것이라는 사실은
 *    장비의 생김새라 서버가 안 줘도 참이다 — 그래서 live 에서도 격자를 그릴 수 있다.
 * 🔴 **「그 칸에 무엇이 담겼나」는 완전히 다른 물음이고, 그쪽은 서버에 없다**(`ChamberRow` 머리말).
 *    ⇒ 이 상수로 격자를 그리되 **칸을 채우지 않고** 「칸 배정 정보 없음」을 함께 적는다.
 * ⚠ fixture(`packages/fixtures/src/chambers.ts` `TRAY_LAYOUT`)에서 가져오지 않는다 — live 화면이
 *   demo 데이터에 의존하면 그 경계가 흐려진다. 두 곳이 같은 칸 이름을 쓰는지는 시험이 지킨다.
 */
export const TRAY_SLOTS: readonly string[] = ['A1', 'A2', 'A3', 'B1', 'B2', 'B3', 'C1', 'C2', 'C3']

/**
 * 대조(Reference) 챔버 칸 — `TRAY_SLOTS` 의 마지막 칸(와이어프레임 표기 `R`).
 * 이 칸은 트레이에 남고 옮겨 다니지 않는다. ⚠ 윗줄 평형추 자리(RSLOT · R″)와는 다른 칸이다
 */
export const REFERENCE_WELL = 'C3'

/**
 * 챔버가 지금 어디 있는가.
 *
 * 🔴 **`undefined` 는 「모른다」다 — 「어디에도 없다」가 아니다.** `ChamberRow.position` 이
 *    없으면(= live 경로) 여기서 판정하지 않는다. 그것을 `'pre'` 로 접으면 **아무 데이터도 없는
 *    화면이 「전부 작업 전」이라고 단정**하게 된다. 이 코드베이스가 `inconclusive` 를 제3값으로
 *    두는 것과 같은 축이다.
 *
 * ⚠ **계약에 구조화된 위치 축이 없다.** demo 의 `position` 은 사람이 읽는 문자열
 *   (「A 트레이」 · 「원심분리기」 · 「C 트레이」)이라 되읽을 수밖에 없다.
 *   → 계약에 `location` enum 이 생기면 이 함수는 사라진다.
 */
export type ChamberWhere = 'pre' | 'inModule' | 'post'

export function whereOf(row: ChamberRow, deviceKey: MonitoringDeviceKey): ChamberWhere | undefined {
  if (row.position === undefined) return undefined
  const position = row.position.trim()
  /* 🔴 `position` 은 언어와 상관없이 한글로 오는 글자라(demo fixture) 비교도 한글 사전으로 한다 — `t()` 면 영어에서 전부 'pre' 로 접힌다 */
  if (position === serverText(TRAY_LABEL_KEY[deviceKey].post)) return 'post'
  if (position === serverText(`module.${deviceKey}`)) return 'inModule'
  return 'pre'
}

/** 모듈별 「작업 전 / 작업 후」 트레이 이름 — 와이어프레임 `LEG_TRAY` 1652행 */
export const TRAY_LABEL_KEY: Record<MonitoringDeviceKey, { pre: MessageKey; post: MessageKey }> = {
  cent: { pre: 'monitoring.tray.C', post: 'monitoring.tray.A' },
  asm: { pre: 'monitoring.tray.A', post: 'monitoring.tray.L' },
  // 염색기는 같은 트레이가 슬라이더로 자리만 옮긴다 — 트레이를 두 번 그리지 않는다(§16-2)
  stain: { pre: 'monitoring.tray.Lrear', post: 'monitoring.tray.Lfront' },
  robot: { pre: 'monitoring.tray.C', post: 'monitoring.tray.A' },
}

/**
 * 트레이 자리 배치 + 챔버 소재로 그림 한 장을 만든다.
 *
 * 🔴 **`layout` 이 없으면(= live) 칸 이름만 있는 빈 격자를 낸다.** 서버에 시료↔칸 대응이
 *    없어서다(`ChamberRow` 머리말). 이때 **화면은 반드시 「칸 배정 정보 없음」을 함께 적는다** —
 *    빈 격자만 그리면 「트레이가 비었다」는 **거짓**이 된다. 실제로는 시료가 들어 있고
 *    우리가 어느 칸인지 모르는 것이다.
 */
export function trayCells(
  layout: readonly TrayCell[] | undefined,
  chambers: readonly ChamberRow[],
  deviceKey: MonitoringDeviceKey,
  side: 'pre' | 'post',
  canViewPii: boolean,
): TrayCellView[] {
  if (layout === undefined) return TRAY_SLOTS.map((slot) => ({ slot, present: false }))

  const bySlot = slotIndex(chambers)

  return layout.map((cell) => {
    const row = cell.sample_id === null ? undefined : bySlot.get(cell.slot)
    if (row === undefined) return { slot: cell.slot, present: false }

    const where = whereOf(row, deviceKey)
    const here = side === 'pre' ? where === 'pre' : where === 'post'
    const verdict: TrayVerdict | null =
      row.verdict === 'pass'
        ? 'pass'
        : row.verdict === 'fail'
          ? 'fail'
          : row.verdict === 'inconclusive'
            ? 'pending'
            : null

    if (here) {
      return {
        slot: cell.slot,
        present: true,
        sublabel: maskPii(row.sample_id, canViewPii),
        verdict,
        // 대조 무늬는 칸 이름이 아니라 **시료가 대조인가**로 정한다 — 그림이 이름을 알 필요가 없다
        ...(row.reference ? { reference: true } : {}),
      }
    }

    // 비어 있는 자리에는 **지금 어디 있는지**를 적는다. 빈 칸으로만 두면 사라진 것처럼 보인다
    return row.position === undefined
      ? { slot: cell.slot, present: false }
      : { slot: cell.slot, present: false, sublabel: row.position }
  })
}

/**
 * 원심 로터 4슬롯 — 모듈 안에 있는 챔버가 차례로 들어간다.
 *
 * 🔴 **`layout` 이 없으면(= live) 네 자리가 전부 빈다.** 「어느 칸이 로터에 실렸나」는
 *    시료↔칸 대응 위에 서는 값이라 그 대응이 없으면 **한 자리도 채울 수 없다**.
 *    ⚠ 이때 그림은 **「적재 정보 없음」을 함께 적어야 한다** — 네 자리가 빈 것을 「로터가 비었다」로
 *      읽으면 거짓이다(IDLE 인 장비에 시료 9건이 있을 수 있다).
 */
export function rotorSlots(
  layout: readonly TrayCell[] | undefined,
  chambers: readonly ChamberRow[],
  canViewPii: boolean,
  spinning: boolean,
): {
  index: 1 | 2 | 3 | 4
  slot?: string
  sublabel?: string
  loaded: boolean
  active?: boolean
  verdict?: 'pass' | 'fail' | null
}[] {
  if (layout === undefined)
    return ([1, 2, 3, 4] as const).map((index) => ({ index, loaded: false }))

  const bySlot = slotIndex(chambers)
  const inside = layout
    .map((cell) => bySlot.get(cell.slot))
    .filter((row): row is ChamberRow => row !== undefined && whereOf(row, 'cent') === 'inModule')

  return ([1, 2, 3, 4] as const).map((index) => {
    const row = inside[index - 1]
    if (row === undefined) return { index, loaded: false }
    return {
      index,
      ...(row.slot === undefined ? {} : { slot: row.slot }),
      sublabel: maskPii(row.sample_id, canViewPii),
      loaded: true,
      active: spinning,
      verdict:
        row.verdict === 'fail'
          ? ('fail' as const)
          : row.verdict === 'pass'
            ? ('pass' as const)
            : null,
    }
  })
}

/**
 * 칸 이름 → 챔버 행. 🔴 **`slot` 이 없는 행은 색인에 들어가지 않는다** — live 경로가 통째로
 * 그렇고, 그때 이 Map 은 **비어 있는 것이 정답**이다(모르는 것을 아무 칸에나 넣지 않는다).
 */
function slotIndex(chambers: readonly ChamberRow[]): Map<string, ChamberRow> {
  const index = new Map<string, ChamberRow>()
  for (const row of chambers) if (row.slot !== undefined) index.set(row.slot, row)
  return index
}

/**
 * 텔레메트리 한 항목을 표기로 바꾼다.
 * 🔴 **없으면 `—`.** 계약이 `telemetry` 를 자유 객체로 두었기 때문에 필드 유무를 화면이 단정할 수 없다.
 */
export function telemetryText(
  snapshot: ModuleSnapshot | undefined,
  field: string,
  unit?: string,
): string {
  const text = telemetryString(snapshot, field)
  if (text === undefined) return t('mask.none')
  return unit === undefined ? text : `${text} ${unit}`
}

/**
 * 텔레메트리 한 칸을 문자열로. 🔴 **객체·배열은 읽지 않는다** —
 * 계약이 자유 객체라 무엇이 올지 모르고, 중첩 값을 화면이 해석하면 그게 추측이 된다.
 */
export function telemetryString(
  snapshot: ModuleSnapshot | undefined,
  field: string,
): string | undefined {
  const value = snapshot?.telemetry?.[field]
  if (typeof value === 'number') return value.toLocaleString(intlLocale())
  if (typeof value === 'string' && value !== '') return value
  return undefined
}

/**
 * 메트릭 묶음 한 벌 만들기.
 *
 * 🔴 **칸이 어디서 오는지는 표가 말한다**(`MetricSpec.source`). 소모품은 `telemetry` 가 아니라
 *    `consumables` 에 산다 — 종전에는 팁·TC 를 `telemetry` 에서 찾아 **영영 `—`** 였다
 *    (서버가 그 자리에 그 칸을 둔 적이 없다 · 2026-09-21 실측).
 * 🔴 **미탑재 소모품은 «줄째» 안 만든다.** 안 쓰는 장비에 「팁 —」을 그리면 「팁을 쓰는데 값을
 *    모른다」로 읽힌다 — 모듈이 그 둘을 칸 유무로 가르는데(`ModuleSnapshot.consumables` 주석)
 *    화면이 도로 합치면 그 구분이 사라진다.
 */
export function metrics(
  snapshot: ModuleSnapshot | undefined,
  spec: readonly {
    key: string
    labelKey: MessageKey
    field: string
    unit?: string
    source?: 'telemetry' | 'consumables' | 'reagent_storage' | 'grips' | 'tip' | 'pose'
  }[],
): MetricItem[] {
  const rows: MetricItem[] = []
  for (const item of spec) {
    /* 파지 · 팁은 영상 아래 줄(`figureMetricTiles`)만 읽는다 — 상세 카드 구역에는 안 선다 */
    if (item.source === 'grips' || item.source === 'tip') continue
    if (item.source === 'pose') {
      /* 위치 봉투 — 🔴 받은 봉투가 없으면 `—`(이 함수의 «없으면 `—`» 규칙 · 「0」으로 접지 않는다) */
      const reading = poseValueOf(snapshot, item.field)
      rows.push({
        key: item.key,
        label: t(item.labelKey),
        value:
          reading.kind === 'absent'
            ? t('mask.none')
            : withUnit(reading.value.toLocaleString(intlLocale()), item.unit),
      })
      continue
    }
    if (item.source === 'consumables' || item.source === 'reagent_storage') {
      const reading =
        item.source === 'consumables'
          ? consumableReading(snapshot, item.field)
          : reagentStorageReading(snapshot, item.field)
      if (reading.kind === 'absent') continue // 미탑재 — 자리를 만들지 않는다
      rows.push({
        key: item.key,
        label: t(item.labelKey),
        value: reading.kind === 'unknown' ? t('mask.none') : withUnit(reading.text, item.unit),
      })
      continue
    }
    rows.push({
      key: item.key,
      label: t(item.labelKey),
      value: telemetryText(snapshot, item.field, item.unit),
    })
  }
  return rows
}

/**
 * 메트릭 **타일** 한 벌 — 장비별 보기 상세 카드(디자인팀 시안 `equipView()` · eric 2026-10-02).
 *
 * 🔴 **칸이 응답에 있는가 / 값이 있는가를 가른다**(팀장 브리프 2026-10-02 「화면의 내용은 API 응답이 정한다」):
 *
 * | 응답 | 타일 |
 * | --- | --- |
 * | 칸 자체가 없다(텔레메트리 키 없음 · 소모품 미선언 · 미러 `null`) | **타일을 만들지 않는다** |
 * | 칸은 있는데 값이 `null`(또는 읽을 수 없는 꼴) | 타일 + 「미수신」(`value: null`) |
 * | 값이 있다 | 타일 + 값 |
 *
 * ⚠ `metrics()`(행 목록 · 패드 · 장비 관점 콘솔)는 텔레메트리가 없을 때 `—` 를 그대로 낸다 — 그쪽은 안 바꿨다.
 *   실물 텔레메트리는 2026-10-02 현재 `{grip_drop_policy}` 한 칸뿐이라(sim `GET /api/v1/status`) live 에서는
 *   텔레메트리 타일이 하나도 안 선다. 소모품 · 시약 보관만 선다.
 */
export interface MetricTile {
  key: string
  label: string
  /** 단위까지 붙은 표기. `null` = 칸은 있는데 값이 안 왔다(「미수신」) */
  value: string | null
}

export function metricTiles(
  snapshot: ModuleSnapshot | undefined,
  spec: readonly {
    key: string
    labelKey: MessageKey
    field: string
    unit?: string
    source?: 'telemetry' | 'consumables' | 'reagent_storage' | 'grips' | 'tip' | 'pose'
  }[],
): MetricTile[] {
  const tiles: MetricTile[] = []
  for (const item of spec) {
    /* 파지 · 팁은 영상 아래 줄(`figureMetricTiles`)만 읽는다 */
    if (item.source === 'grips' || item.source === 'tip') continue
    const label = t(item.labelKey)
    if (item.source === 'pose') {
      /* 위치 봉투 — 칸이 없으면 타일을 만들지 않는다(이 함수의 규칙 그대로) */
      const reading = poseValueOf(snapshot, item.field)
      if (reading.kind === 'absent') continue
      tiles.push({
        key: item.key,
        label,
        value: withUnit(reading.value.toLocaleString(intlLocale()), item.unit),
      })
      continue
    }
    if (item.source === 'consumables' || item.source === 'reagent_storage') {
      const reading =
        item.source === 'consumables'
          ? consumableReading(snapshot, item.field)
          : reagentStorageReading(snapshot, item.field)
      if (reading.kind === 'absent') continue
      tiles.push({
        key: item.key,
        label,
        value: reading.kind === 'unknown' ? null : withUnit(reading.text, item.unit),
      })
      continue
    }
    const bag = snapshot?.telemetry
    if (bag == null || !(item.field in bag)) continue
    const text = telemetryString(snapshot, item.field)
    tiles.push({
      key: item.key,
      label,
      value: text === undefined ? null : withUnit(text, item.unit),
    })
  }
  return tiles
}

function withUnit(text: string, unit: string | undefined): string {
  return unit === undefined ? text : `${text} ${unit}`
}

/**
 * 소모품 한 칸의 **세 갈래**(`ModuleSnapshot.consumables`).
 *
 * | 반환 | 실물 | 뜻 |
 * |---|---|---|
 * | `absent` | 키가 **없다** | 이 장비가 그 소모품을 **안 쓴다**(미선언) |
 * | `unknown` | 키가 있고 `null` | 쓰긴 쓰는데 **값을 모른다** |
 * | `value` | 키가 있고 숫자 | 그 값 |
 *
 * 🔴 **둘을 합치지 마라.** module-core 가 2026-09-20 부터 «칸째» 빼는 규칙을 집행한다
 *    (`app/api/routes.py` `status_body` — 종전에는 안 쓰는 모듈도 `tips_remaining: null` 을
 *    내보내 「없다」와 「모른다」가 선로에서 같았다). 실측:
 *    원심 `{"reagents":null}` · 염색 `{"reagents":null,"tips_remaining":null}` ·
 *    어셈블 `{"reagents":null,"tc_remaining":null}`.
 */
export type ConsumableReading =
  { kind: 'absent' } | { kind: 'unknown' } | { kind: 'value'; value: number; text: string }

export function consumableReading(
  snapshot: ModuleSnapshot | undefined,
  field: string,
): ConsumableReading {
  const bag = snapshot?.consumables
  if (bag == null || !(field in bag)) return { kind: 'absent' }
  const value = bag[field]
  if (typeof value !== 'number') return { kind: 'unknown' }
  return { kind: 'value', value, text: value.toLocaleString(intlLocale()) }
}

/**
 * 시약 보관 온도 미러 한 칸. **같은 세 갈래**를 쓴다 —
 * 그 능력이 없는 모듈은 `reagent_storage: null` 이라 `absent`(미탑재),
 * 능력은 있는데 값이 안 찼으면 `null` 이라 `unknown`(모름)이다.
 * 🔴 `at_target`·`state` 는 실물이 `null` 로 오므로 수치 축인 여기서 읽지 않는다.
 */
export function reagentStorageReading(
  snapshot: ModuleSnapshot | undefined,
  field: string,
): ConsumableReading {
  const mirror = snapshot?.reagent_storage
  if (mirror == null || !(field in mirror)) return { kind: 'absent' }
  const value = (mirror as Record<string, unknown>)[field]
  if (typeof value !== 'number') return { kind: 'unknown' }
  return { kind: 'value', value, text: value.toLocaleString(intlLocale()) }
}

/**
 * 소모품 수치. 🔴 **「미탑재」와 「모름」을 둘 다 `undefined` 로 접는다** — 그 구분이 필요하면
 * `consumableReading()` 을 써라. 그림처럼 수치 하나만 쓰는 자리에만 남긴다.
 */
export function consumableNumber(
  snapshot: ModuleSnapshot | undefined,
  field: string,
): number | undefined {
  const reading = consumableReading(snapshot, field)
  return reading.kind === 'value' ? reading.value : undefined
}

/** 텔레메트리 수치. 문자열로 와도 숫자면 받는다 */
export function telemetryNumber(
  snapshot: ModuleSnapshot | undefined,
  field: string,
): number | undefined {
  const value = snapshot?.telemetry?.[field]
  if (typeof value === 'number') return value
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
    return Number(value)
  }
  return undefined
}

/**
 * 6축 레일 정차점 — 원심 · 어셈블러 · 염색기 (와이어프레임 1595행 `STOP_X`).
 *
 * 🔴 **값을 그림 쪽에 박지 마라.** 레일 카드를 어느 앱이 그리든 정차점은 여기 하나다.
 * ⚠ 이것은 3축 로봇 레일(`GantryFigure`)이 아니라 **모듈 사이를 오가는 6축 로봇**의 레일이다.
 */
export const RAIL_STOPS: readonly { key: string; left: string }[] = [
  { key: 'cent', left: '16.67%' },
  { key: 'asm', left: '50%' },
  { key: 'stain', left: '83.33%' },
]

/** 파킹 자리 — 첫 정차점 왼쪽 바깥. 레일의 «구성 요소»(P 표지) 자리다 — 로봇이 거기 있다는 뜻이 아니다 */
export const RAIL_PARK_LEFT = 'calc(16.67% - 46px)'

/**
 * 레일 마커를 놓을 가로 위치. **모르면 `undefined`** — 마커를 그리지 않고 「위치 미발행」을 적는다.
 * 🔴 **텔레메트리에 `rail_stop` 이 있고 정차점 이름일 때만 옮긴다.** 파킹(`park`)도 서버가 그렇게 말했을 때만이다.
 * ⚠ 2026-10-06 까지는 값이 없으면 **파킹에 그렸다**(`?? 'park'`) — dev2 는 이 값을 한 번도 안 줘서(telemetry =
 *   `{grip_drop_policy}` 하나) 장비 관점 레일의 로봇이 **늘 P 에 서 있는 것처럼** 보였다. 「모름」을 「파킹」으로 접은 것이라 걷었다.
 *   위치는 module-core 장부(`arm_track`)에 있고 `/status` 에 안 실린다 — `docs/backend-requests.csv` BR-114.
 */
export function railStopLeft(snapshot: ModuleSnapshot | undefined): string | undefined {
  const at = telemetryString(snapshot, 'rail_stop')
  if (at === undefined) return undefined
  if (at === 'park') return RAIL_PARK_LEFT
  return RAIL_STOPS.find((stop) => stop.key === at)?.left
}

/**
 * 6축 그림의 기준 자세 — 관절값을 모를 때 그리는 «고정 아이콘»의 자세다. 지금 자세가 아니다.
 * 이 자세로 그릴 때는 반드시 「자세 미발행」을 같이 적는다(`jointValues` 가 `undefined` 를 낸 때).
 */
export const ARM_HOME: readonly number[] = [0, -90, 90, -90, -90, 0]

/**
 * 6축 관절값 J1~J6. **하나도 없으면 `undefined`** — 그리는 쪽이 기준 자세 고정 아이콘 + 「자세 미발행」으로 간다.
 * ⚠ 2026-10-06 까지는 없으면 `ARM_HOME` 을 «값처럼» 돌려줘서 그림이 지금 자세인 척했다. 일부만 오면 빈 관절만 기준 자세로 채운다.
 */
export function jointValues(snapshot: ModuleSnapshot | undefined): readonly number[] | undefined {
  const joints = [1, 2, 3, 4, 5, 6].map((n) => telemetryNumber(snapshot, `joint_${String(n)}`))
  return joints.every((v) => v === undefined)
    ? undefined
    : joints.map((v, i) => v ?? ARM_HOME[i] ?? 0)
}
